/**
 * Push Notification utilities for PWA
 */

import api from './axiosConfig';

// VAPID keys - production keys generated for Connect
const VAPID_PUBLIC_KEY = 'BKLJM3Mq_uKjwTFQ3H9VSts1RnCSNaQ_xubPOXLyGEV2mdSf5J_Iang1_3lmxkFXROu1KLgTJR5qil5QXgYHqHM';

/**
 * Check if push notifications are supported
 * @returns {boolean}
 */
export const isPushNotificationSupported = () => {
    return 'serviceWorker' in navigator && 'PushManager' in window;
};

/**
 * Check if user has granted notification permission
 * @returns {boolean}
 */
export const isNotificationPermissionGranted = () => {
    return Notification.permission === 'granted';
};

/**
 * Request notification permission from user
 * @returns {Promise<string>} Permission status: 'granted', 'denied', or 'default'
 */
export const requestNotificationPermission = async () => {
    if (!isPushNotificationSupported()) {
        throw new Error('Push notifications are not supported');
    }

    try {
        const permission = await Notification.requestPermission();
        return permission;
    } catch (error) {
        console.error('Error requesting notification permission:', error);
        throw error;
    }
};

/**
 * Register service worker and get push subscription
 * @returns {Promise<PushSubscription>}
 */
export const subscribeToPushNotifications = async () => {
    if (!isPushNotificationSupported()) {
        throw new Error('Push notifications are not supported');
    }

    try {
        // Register service worker
        const registration = await navigator.serviceWorker.register('/sw.js');
        
        // Wait for service worker to be ready
        await navigator.serviceWorker.ready;

        // Reuse existing subscription on this device if present.
        const existingSubscription = await registration.pushManager.getSubscription();
        if (existingSubscription) {
            return existingSubscription;
        }

        // Subscribe to push notifications
        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
        });

        console.log('Push subscription created:', subscription);
        return subscription;
    } catch (error) {
        console.error('Error subscribing to push notifications:', error);
        throw error;
    }
};

/**
 * Unsubscribe from push notifications
 * @returns {Promise<boolean>}
 */
export const unsubscribeFromPushNotifications = async () => {
    if (!isPushNotificationSupported()) {
        return false;
    }

    try {
        const registration = await navigator.serviceWorker.getRegistration('/sw.js');
        if (registration) {
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
                const result = await subscription.unsubscribe();
                console.log('Unsubscribed from push notifications');
                return result;
            }
        }
        return false;
    } catch (error) {
        console.error('Error unsubscribing from push notifications:', error);
        throw error;
    }
};

/**
 * Get current push subscription
 * @returns {Promise<PushSubscription|null>}
 */
export const getCurrentPushSubscription = async () => {
    if (!isPushNotificationSupported()) {
        return null;
    }

    try {
        const registration = await navigator.serviceWorker.getRegistration('/sw.js');
        if (registration) {
            return await registration.pushManager.getSubscription();
        }
        return null;
    } catch (error) {
        console.error('Error getting push subscription:', error);
        return null;
    }
};

/**
 * Send push subscription to server
 * @param {PushSubscription} subscription
 * @returns {Promise<void>}
 */
export const sendSubscriptionToServer = async (subscription) => {
    try {
        await api.post('/api/notifications/subscribe', {
            subscription: subscription.toJSON()
        });
    } catch (error) {
        throw new Error('Failed to send subscription to server');
    }
};

/**
 * Remove push subscription from server
 * @param {PushSubscription|Object|null} subscription
 * @returns {Promise<void>}
 */
export const removeSubscriptionFromServer = async (subscription = null) => {
    try {
        const payload = subscription
            ? (typeof subscription.toJSON === 'function' ? subscription.toJSON() : subscription)
            : null;

        if (!payload || !payload.endpoint) {
            throw new Error('Missing subscription payload for unsubscribe');
        }

        await api.post('/api/notifications/unsubscribe', {
            subscription: payload
        });
    } catch (error) {
        throw new Error('Failed to remove subscription from server');
    }
};

/**
 * Test push notification
 * @returns {Promise<void>}
 */
export const testPushNotification = async () => {
    try {
        await api.post('/api/notifications/test-push');
    } catch (error) {
        throw new Error('Failed to send test notification');
    }
};

/**
 * Convert VAPID key to Uint8Array
 * @param {string} base64String
 * @returns {Uint8Array}
 */
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

/**
 * Show a local notification (fallback when push is not available)
 * @param {string} title
 * @param {object} options
 */
export const showLocalNotification = (title, options = {}) => {
    if (Notification.permission === 'granted') {
        const notification = new Notification(title, {
            icon: '/logo192.png',
            badge: '/caregiver-icon.svg',
            ...options
        });

        // Auto-close after 5 seconds if not critical
        if (options.priority !== 'critical' && options.priority !== 'high') {
            setTimeout(() => notification.close(), 5000);
        }

        return notification;
    }
};

/**
 * Check if app is installed as PWA and push notifications are setup
 * @returns {Promise<boolean>}
 */
export const isPWAWithPushEnabled = async () => {
    // Check if running as PWA
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  window.navigator.standalone === true;
    
    if (!isPWA) return false;

    // Check if push notifications are supported and permission granted
    const isSupported = isPushNotificationSupported();
    const hasPermission = isNotificationPermissionGranted();
    const hasSubscription = await getCurrentPushSubscription();

    return isSupported && hasPermission && hasSubscription !== null;
};
